import { useState } from 'react'
import {
    useAdminAffiliateCommissions,
    useAdminAffiliatePayouts,
    useAdminAffiliateReferrals,
} from 'src/hooks/useAdminAffiliates'
import { formatBps, formatCents, formatDay } from 'src/utils/affiliateFormat'
import {
    CommissionKindBadge,
    CommissionStatusBadge,
    PayoutStatusBadge,
    ReferralStatusBadge,
    errorMessage,
} from '../affiliate/labels'
import { Badge, Card, EmptyRow, ErrorText, InlineSpinner, Muted, Pager, Row, Table, TableWrap, Tabs } from '../affiliate/ui'
import { TransfersCell } from './PayoutsTab'
import { Mono, Negative } from './shared'

const reviewTone = { none: 'neutral', held: 'yellow', cleared: 'green', rejected: 'red' } as const

const ReferralsTable = ({ affiliateId }: { affiliateId: string }) => {
    const [page, setPage] = useState(1)
    const { data, isLoading, error } = useAdminAffiliateReferrals(affiliateId, page)
    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{errorMessage(error)}</ErrorText>
    return (
        <>
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Discord</th>
                            <th>Code</th>
                            <th>Status</th>
                            <th>Joined</th>
                            <th className="num">Months paid</th>
                            <th className="num">Earned</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.results.length > 0 ? (
                            data.results.map((referral) => (
                                <tr key={referral.id}>
                                    <td>
                                        {referral.displayName}
                                        {referral.blocked && (
                                            <Badge $tone="red" style={{ marginLeft: 6 }}>
                                                Blocked
                                            </Badge>
                                        )}
                                    </td>
                                    <td style={{ overflowWrap: 'anywhere' }}>{referral.email || '—'}</td>
                                    <td>{referral.discordUsername ? `@${referral.discordUsername}` : '—'}</td>
                                    <td>{referral.code || '—'}</td>
                                    <td>
                                        <ReferralStatusBadge status={referral.status} />
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(referral.joinedAt)}</td>
                                    <td className="num">{referral.monthsPaid}</td>
                                    <td className="num">{formatCents(referral.earnedCents)}</td>
                                </tr>
                            ))
                        ) : (
                            <EmptyRow colSpan={8}>No referrals yet.</EmptyRow>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
            {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
        </>
    )
}

const CommissionsTable = ({ affiliateId }: { affiliateId: string }) => {
    const [page, setPage] = useState(1)
    const { data, isLoading, error } = useAdminAffiliateCommissions(affiliateId, page)
    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{errorMessage(error)}</ErrorText>
    return (
        <>
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Kind</th>
                            <th>Status</th>
                            <th className="num">Amount</th>
                            <th className="num">Base</th>
                            <th className="num">Rate</th>
                            <th>Referral</th>
                            <th>Flags</th>
                            <th>Review</th>
                            <th>Created</th>
                            <th>Available</th>
                            <th>Note</th>
                            <th>Ids</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.results.length > 0 ? (
                            data.results.map((commission) => (
                                <tr key={commission.id}>
                                    <td>
                                        <CommissionKindBadge kind={commission.kind} />
                                    </td>
                                    <td>
                                        <CommissionStatusBadge status={commission.status} />
                                    </td>
                                    <td className="num">
                                        <Negative $negative={commission.amountCents < 0}>
                                            {formatCents(commission.amountCents)}
                                        </Negative>
                                        {commission.reversedCents > 0 && (
                                            <Muted style={{ fontSize: 12 }}>
                                                of {formatCents(commission.originalAmountCents)}
                                            </Muted>
                                        )}
                                    </td>
                                    <td className="num">
                                        {commission.baseCents == null ? '—' : formatCents(commission.baseCents)}
                                    </td>
                                    <td className="num">{formatBps(commission.rateBps)}</td>
                                    <td style={{ overflowWrap: 'anywhere' }}>
                                        {commission.referral
                                            ? commission.referral.email ||
                                              (commission.referral.discordUsername && `@${commission.referral.discordUsername}`) ||
                                              commission.referral.displayName
                                            : '—'}
                                    </td>
                                    <td>
                                        <Row $gap={4}>
                                            {commission.flags.length > 0
                                                ? commission.flags.map((flag) => (
                                                      <Badge key={flag} $tone="red">
                                                          {flag}
                                                      </Badge>
                                                  ))
                                                : '—'}
                                        </Row>
                                    </td>
                                    <td>
                                        {commission.review === 'none' ? (
                                            '—'
                                        ) : (
                                            <Badge $tone={reviewTone[commission.review]}>{commission.review}</Badge>
                                        )}
                                    </td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(commission.createdAt)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(commission.availableAt)}</td>
                                    <td style={{ minWidth: 140, overflowWrap: 'anywhere' }}>{commission.note || '—'}</td>
                                    <td>
                                        {commission.invoiceId && <div><Mono>{commission.invoiceId}</Mono></div>}
                                        {commission.chargeId && <div><Mono>{commission.chargeId}</Mono></div>}
                                        {commission.payoutId && <div><Mono>payout {commission.payoutId}</Mono></div>}
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <EmptyRow colSpan={12}>No commissions yet.</EmptyRow>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
            {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
        </>
    )
}

const PayoutsTable = ({ affiliateId }: { affiliateId: string }) => {
    const [page, setPage] = useState(1)
    const { data, isLoading, error } = useAdminAffiliatePayouts(affiliateId, page)
    if (isLoading) return <InlineSpinner />
    if (error) return <ErrorText>{errorMessage(error)}</ErrorText>
    return (
        <>
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Status</th>
                            <th className="num">Amount</th>
                            <th>Created</th>
                            <th>Paid</th>
                            <th>Failure</th>
                            <th>Transfers</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data && data.results.length > 0 ? (
                            data.results.map((payout) => (
                                <tr key={payout.id}>
                                    <td>
                                        <PayoutStatusBadge status={payout.status} />
                                        {payout.finalPayout && (
                                            <Badge $tone="neutral" style={{ marginLeft: 6 }}>
                                                Final
                                            </Badge>
                                        )}
                                    </td>
                                    <td className="num">{formatCents(payout.amountCents)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(payout.createdAt)}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{formatDay(payout.paidAt)}</td>
                                    <td style={{ minWidth: 140, overflowWrap: 'anywhere' }}>{payout.failureReason || '—'}</td>
                                    <td>
                                        <TransfersCell payout={payout} />
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <EmptyRow colSpan={6}>No payouts yet.</EmptyRow>
                        )}
                    </tbody>
                </Table>
            </TableWrap>
            {data && <Pager page={data.page} totalPages={data.totalPages} onChange={setPage} />}
        </>
    )
}

type SubTab = 'referrals' | 'commissions' | 'payouts'

export const DetailTables = ({ affiliateId }: { affiliateId: string }) => {
    const [tab, setTab] = useState<SubTab>('referrals')
    return (
        <Card>
            <Tabs<SubTab>
                tabs={[
                    { value: 'referrals', label: 'Referrals' },
                    { value: 'commissions', label: 'Commissions' },
                    { value: 'payouts', label: 'Payouts' },
                ]}
                value={tab}
                onChange={setTab}
            />
            {tab === 'referrals' && <ReferralsTable affiliateId={affiliateId} />}
            {tab === 'commissions' && <CommissionsTable affiliateId={affiliateId} />}
            {tab === 'payouts' && <PayoutsTable affiliateId={affiliateId} />}
        </Card>
    )
}
